// Marketplace Actions Component
// Feature: 006-marketplace-management

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, RotateCcw, Clock, CheckCircle } from 'lucide-react';
import type { MarketplaceItemStatus } from '@/types/marketplaceAdmin';

interface MarketplaceActionsProps {
  currentStatus: MarketplaceItemStatus;
  onDelete: (reason: string) => Promise<{ success: boolean; error?: string }>;
  onRestore: () => Promise<{ success: boolean; error?: string }>;
  onExpire: () => Promise<{ success: boolean; error?: string }>;
  onReactivate: () => Promise<{ success: boolean; error?: string }>;
}

export function MarketplaceActions({
  currentStatus,
  onDelete,
  onRestore,
  onExpire,
  onReactivate,
}: MarketplaceActionsProps) {
  const { t } = useTranslation('marketplaceAdmin');
  const [showConfirm, setShowConfirm] = useState<'delete' | 'restore' | 'expire' | 'reactivate' | null>(
    null
  );
  const [deleteReason, setDeleteReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for focus management
  const triggerButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Focus management: focus first interactive element when modal opens
  useEffect(() => {
    if (showConfirm) {
      // For delete, focus the textarea; for others, focus cancel button
      if (showConfirm === 'delete' && textareaRef.current) {
        textareaRef.current.focus();
      } else if (cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
    }
  }, [showConfirm]);

  // Handle opening modal and storing trigger ref
  const openConfirm = (type: 'delete' | 'restore' | 'expire' | 'reactivate', buttonRef: HTMLButtonElement) => {
    triggerButtonRef.current = buttonRef;
    setShowConfirm(type);
  };

  // Handle closing modal and restoring focus
  const closeConfirm = () => {
    setShowConfirm(null);
    setDeleteReason('');
    setError(null);
    // Restore focus to the button that opened the modal
    if (triggerButtonRef.current) {
      triggerButtonRef.current.focus();
      triggerButtonRef.current = null;
    }
  };

  const handleDelete = async () => {
    if (!deleteReason.trim()) {
      setError(t('messages.reasonRequired'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    try {
      const result = await onDelete(deleteReason);
      if (result.success) {
        closeConfirm();
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestore = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await onRestore();
      if (result.success) {
        closeConfirm();
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExpire = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await onExpire();
      if (result.success) {
        closeConfirm();
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await onReactivate();
      if (result.success) {
        closeConfirm();
      } else {
        setError(result.error || t('messages.error'));
      }
    } catch (err) {
      setError(t('messages.error'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3" role="group" aria-label={t('actions.statusActions')}>
        {/* Active status: Can delete or expire */}
        {currentStatus === 'active' && (
          <>
            <button
              onClick={(e) => openConfirm('expire', e.currentTarget)}
              aria-label={t('actions.expireAria')}
              className="flex items-center gap-2 px-4 py-2 border-2 border-yellow-600 text-yellow-600 bg-white rounded-lg hover:bg-yellow-50 transition font-medium"
            >
              <Clock size={18} aria-hidden="true" />
              {t('actions.expire')}
            </button>
            <button
              onClick={(e) => openConfirm('delete', e.currentTarget)}
              aria-label={t('actions.deleteAria')}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-50 transition font-medium"
            >
              <Trash2 size={18} aria-hidden="true" />
              {t('actions.delete')}
            </button>
          </>
        )}

        {/* Expired status: Can reactivate or delete */}
        {currentStatus === 'expired' && (
          <>
            <button
              onClick={(e) => openConfirm('reactivate', e.currentTarget)}
              aria-label={t('actions.reactivateAria')}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
            >
              <CheckCircle size={18} aria-hidden="true" />
              {t('actions.reactivate')}
            </button>
            <button
              onClick={(e) => openConfirm('delete', e.currentTarget)}
              aria-label={t('actions.deleteAria')}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-50 transition font-medium"
            >
              <Trash2 size={18} aria-hidden="true" />
              {t('actions.delete')}
            </button>
          </>
        )}

        {/* Deleted status: Can restore */}
        {currentStatus === 'deleted' && (
          <button
            onClick={(e) => openConfirm('restore', e.currentTarget)}
            aria-label={t('actions.restoreAria')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium"
          >
            <RotateCcw size={18} aria-hidden="true" />
            {t('actions.restore')}
          </button>
        )}
      </div>

      {/* Confirmation Modals */}
      {showConfirm && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dialog-title"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => {
            // Close on backdrop click
            if (e.target === e.currentTarget) {
              closeConfirm();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeConfirm();
            }
          }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 id="dialog-title" className="text-lg font-bold text-neutral-900 mb-4">
              {t(`confirm.${showConfirm}.title`)}
            </h3>

            {showConfirm === 'delete' ? (
              <>
                <p className="text-neutral-700 mb-4">{t('confirm.delete.message')}</p>
                <div className="mb-4">
                  <label
                    htmlFor="delete-reason"
                    className="block text-sm font-medium text-neutral-700 mb-2"
                  >
                    {t('confirm.delete.reasonLabel')}
                  </label>
                  <textarea
                    ref={textareaRef}
                    id="delete-reason"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder={t('confirm.delete.reasonPlaceholder')}
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={isProcessing}
                  />
                </div>
              </>
            ) : (
              <p className="text-neutral-700 mb-4">
                {t(`confirm.${showConfirm}.message`)}
              </p>
            )}

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <button
                ref={cancelButtonRef}
                onClick={closeConfirm}
                disabled={isProcessing}
                className="px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50"
              >
                {t(`confirm.${showConfirm}.cancel`)}
              </button>
              <button
                onClick={() => {
                  if (showConfirm === 'delete') handleDelete();
                  else if (showConfirm === 'restore') handleRestore();
                  else if (showConfirm === 'expire') handleExpire();
                  else if (showConfirm === 'reactivate') handleReactivate();
                }}
                disabled={isProcessing}
                className={`px-4 py-2 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center gap-2 ${
                  showConfirm === 'restore' || showConfirm === 'reactivate'
                    ? 'bg-green-600 hover:bg-green-700'
                    : showConfirm === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {isProcessing && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {t(`confirm.${showConfirm}.confirm`)}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
